import clsx from 'clsx';
import Image from 'next/image';

const PlusIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      stroke="#9BFF9B"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 5v14M5 12h14"
    />
  </svg>
);

/**
 * Which slot each card type occupies in the toggle, left to right. Doubles as
 * the value of `selectedIndex`.
 */
export const CARD_INDEX = { downsell: 0, main: 1, upsell: 2 };

/**
 * Presentational card for the upsell toggle.
 */
export default function ProductCard({
  showOffsetBlock,
  productData,
  selectedIndex,
  type,
  variantSize,
  priceDifference,
  showDownsellCard,
  onCardClick
}) {
  const { tag_text, details, image, shopifyProduct } = productData;

  const isMain = type === 'main';
  const isUpsell = type === 'upsell';
  const cardDataCyMap = {
    upsell: 'upsell-toggle-card-upsell',
    downsell: 'upsell-toggle-card-downsell'
  };
  const cardDataCy = cardDataCyMap[type];

  const productTitle =
    shopifyProduct[0]?.productType === 'Mattress'
      ? shopifyProduct[0]?.title.replace(' Mattress', '')
      : shopifyProduct[0]?.title;

  const isSelected = selectedIndex === CARD_INDEX[type];
  const cardLabel = isMain
    ? productTitle
    : `${productTitle} ${variantSize ? `(${variantSize})` : ''}`;

  const borderClasses = clsx(
    'cursor-pointer',
    isSelected
      ? 'border-light-green bg-parlor-green-95'
      : 'border-parlor-green-80',
    !isSelected &&
      showOffsetBlock &&
      'bg-parlor-green-115 hover:bg-parlor-green-95',
    !isSelected && !showOffsetBlock && 'hover:bg-parlor-green-95'
  );

  const shouldShowVariantSize = isUpsell;
  const shouldShowPlusIcon = isUpsell || (isMain && showDownsellCard);

  return (
    <div
      className={`product-card-${type} group grid`}
      data-cy={cardDataCy}
      tabIndex={0}
      role="button"
      aria-pressed={isSelected}
      aria-label={cardLabel}
      onClick={onCardClick}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && onCardClick) {
          e.preventDefault();
          onCardClick();
        }
      }}
    >
      {tag_text && (
        <div className={`relative grid grid-cols-2 gap-x-1`}>
          <span className="bg-light-green sh13 flex items-center justify-center py-0.5 text-black">
            {tag_text}
          </span>
        </div>
      )}
      <div
        className={`border-2 ${borderClasses} flex flex-col justify-between`}
      >
        {/* Upsell Toggle Title, Size and Price Difference */}
        <div className="px-2 pt-2 pl-3">
          <div className="flex w-full justify-between">
            <div className="sh12 mr-2 text-white">{productTitle}</div>
            {isSelected ? (
              <div className="border-light-green bg-parlor-green relative z-20 flex h-4 w-4 rounded-full border">
                <div className="bg-light-green z-30 mx-auto my-auto h-2 w-2 rounded-full"></div>
              </div>
            ) : (
              <div className="bg-parlor-green-80 z-20 h-4 w-4 rounded-full border border-white"></div>
            )}
          </div>
          {/* min-h matches the price-r-label line-height, so this row holds its
              space while the selection is still resolving. */}
          <div className="min-h-6">
            {isSelected ? (
              <div className="text-dawn-grey-60 price-r-label">This item</div>
            ) : (
              <div className="flex">
                {priceDifference > 0 && (
                  <div className="price-s-form self-center pr-0.5">
                    +${priceDifference}
                  </div>
                )}
                {shouldShowVariantSize && variantSize && (
                  <div className="text-dawn-grey-60 price-r-label">
                    ({variantSize})
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Upsell Toggle Image */}
        <div>
          <Image
            src={image?.url}
            width="348"
            height="116"
            sizes="(min-width: 1536px) 171px, (min-width: 1280px) 185px, (min-width: 1024px) 150px, (min-width: 768px) 266px, (min-width: 640px) 202px, 290px"
            alt={image?.title.replace(/\.[^/.]+$/, '')}
          />
          {/* Upsell Toggle Details */}
          <div className="px-2 pb-2">
            {details?.map((item, index) => (
              <div key={index} className="flex items-center first:mb-1">
                {shouldShowPlusIcon && <PlusIcon />}
                <div className="body-s pl-1 text-white">{item.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
